import React, { useState, useEffect, Fragment } from 'react';
import { User, Project, Location } from '../../types';
import { apiService, ApiTeam } from '../../services/api';
import { useToast } from '../ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Users, 
  Clock, 
  Package, 
  FileSpreadsheet,
  Play,
  Square,
  Coffee,
  UserCheck,
  LogOut,
  RefreshCw,
  Loader2,
  AlertCircle
} from 'lucide-react';

// Import tab components
import { TeamManagementTab } from './tabs/TeamManagementTab';
import { PersonalTrackingTab } from './tabs/PersonalTrackingTab';
import { EndOfDayTab } from './tabs/EndOfDayTab';

interface RefactoredSupervisorDashboardProps {
  currentUser: User;
  onLogout: () => void;
}

export const RefactoredSupervisorDashboard: React.FC<RefactoredSupervisorDashboardProps> = ({
  currentUser,
  onLogout
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [teams, setTeams] = useState<ApiTeam[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  const { toast } = useToast();

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const [projectsData, locationsData] = await Promise.all([
        apiService.getProjects(),
        apiService.getLocations()
      ]);
      
      setProjects(projectsData);
      setLocations(locationsData);
      
      // Try to load supervisor teams, but don't fail if it doesn't work
      try {
        const teamsData = await apiService.getSupervisorTeams();
        setTeams(teamsData);
        
        // Set default active tab
        if (teamsData.length > 0 && !activeTab) {
          setActiveTab(`team-${teamsData[0].id}`);
        } else if (!activeTab) {
          setActiveTab('personal');
        }
      } catch (teamsError) {
        console.error('Error loading supervisor teams:', teamsError);
        // Fallback: Get teams where supervisor is directly assigned
        try {
          const allTeams = await apiService.getTeams();
          const supervisorTeams = allTeams.filter(team => team.supervisorId === currentUser.id);
          setTeams(supervisorTeams);
          
          if (supervisorTeams.length > 0 && !activeTab) {
            setActiveTab(`team-${supervisorTeams[0].id}`);
          } else if (!activeTab) {
            setActiveTab('personal');
          }
        } catch (fallbackError) {
          console.error('Error loading teams fallback:', fallbackError);
          setTeams([]);
          setActiveTab('personal');
        }
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele inițiale',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadInitialData();
    // Trigger refresh in active tab
    window.dispatchEvent(new CustomEvent('supervisor-refresh'));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">
                Dashboard Supervisor
              </h1>
              <Badge variant="secondary" className="text-sm">
                {currentUser.name}
              </Badge>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                Actualizează
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={onLogout}
                className="flex items-center gap-2 text-red-600 hover:text-red-700"
              >
                <LogOut className="h-4 w-4" />
                Deconectare
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className={`grid w-full mx-auto ${teams.length === 0 ? 'grid-cols-1 max-w-xs' : 'grid-flow-col auto-cols-fr overflow-x-auto'}`}>
            {/* Team tabs */}
            {teams.map((team) => (
              <React.Fragment key={team.id}>
                <TabsTrigger value={`team-${team.id}`} className="flex items-center gap-2 min-w-max">
                  <Users className="h-4 w-4" />
                  <span className="hidden lg:inline">{team.name}</span>
                  <span className="lg:hidden">{team.name.slice(0, 10)}</span>
                </TabsTrigger>
                <TabsTrigger value={`endday-${team.id}`} className="flex items-center gap-2 min-w-max">
                  <FileSpreadsheet className="h-4 w-4" />
                  <span className="hidden lg:inline">Închidere {team.name}</span>
                  <span className="lg:hidden">Final {team.name.slice(0, 8)}</span>
                </TabsTrigger>
              </React.Fragment>
            ))}
            
            <TabsTrigger value="personal" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span className="hidden sm:inline">Pontaj Personal</span>
              <span className="sm:hidden">Personal</span>
            </TabsTrigger>
          </TabsList>

          {/* Team Management Tabs */}
          {teams.map((team) => (
            <React.Fragment key={team.id}>
              <TabsContent value={`team-${team.id}`} className="space-y-4">
                <TeamManagementTab
                  supervisorId={currentUser.id}
                  supervisorUser={currentUser}
                  projects={projects}
                  locations={locations}
                  teamId={team.id}
                  teamName={team.name}
                />
              </TabsContent>
              
              {/* End of Day Tab for this team */}
              <TabsContent value={`endday-${team.id}`} className="space-y-4">
                <EndOfDayTab
                  supervisorId={currentUser.id}
                  currentUser={currentUser}
                  teamId={team.id}
                  teamName={team.name}
                />
              </TabsContent>
            </React.Fragment>
          ))}

          {/* Personal Tracking Tab */}
          <TabsContent value="personal" className="space-y-4">
            <PersonalTrackingTab
              user={currentUser}
              projects={projects}
              locations={locations}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};